import React, { useState } from 'react';

import { Col, Flex, Row, Spacer } from '@amzn/stencil-react-components/layout';
import { Text, Label, H4 } from '@amzn/stencil-react-components/text';
import { EmailReportWidget } from './shared/EmailReportWidget';
import { IconQuestionCircle } from '@amzn/stencil-react-components/icons';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import {
  TableElement,
  TbodyElement,
  TdElement,
  TheadElement,
  TrElement,
} from '@amzn/stencil-react-components/table';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';
import moment from 'moment';
import { useMemo } from 'react';
import { formatReportItems } from 'src/util/lookback_report_utils';
import {useGlobalContext, useUserDataContext} from '../hooks';
import { useTranslation } from 'react-i18next';
import { Select } from '@amzn/stencil-react-components/form';
import { ProductivityReportItem } from 'src/types/backendService';
import {WA_WAREHOUSE_ALLOW_LIST} from "src/constants/featureGates";
import { phase1Date } from 'src/constants/featureGates';
import { getPerformanceWeekStart } from 'src/util/date_time_utils';

const IconWithTooltip = withTooltip()(IconQuestionCircle);

const SEVEN_DAYS_REVIEW_PERIOD = 7;
const FOURTEEN_DAYS_REVIEW_PERIOD = 14;

/* Max number of days to display daily productivity data for.
  * This will ensure future looking periods will be expanded to 14 days.
  */
const getSPPRPeriodDays = (periodStart: number) => {
  const SPPR_PHASE_ROLLOUT_DATE = new Date(phase1Date);
  return periodStart >= +SPPR_PHASE_ROLLOUT_DATE ? FOURTEEN_DAYS_REVIEW_PERIOD : SEVEN_DAYS_REVIEW_PERIOD;
}

const isFourteenDaysReviewPeriod = (periodStart: number) => {
  return getSPPRPeriodDays(periodStart! * 1000) === FOURTEEN_DAYS_REVIEW_PERIOD;
}

const getPercentileString = (percentToPeers?: number) => {
  return percentToPeers ? `${Math.round(percentToPeers)}%` : '';
}

const ProductivityDisclosure = ({}) => {
  return (
    <Text fontWeight="light">
      If you have questions about eligibility criteria or the weekly productivity review process,
      please refer to the informational available in your WorkDocs Folder in A to Z.
    </Text>
  );
};

export const ProductivityLookbackReport = ({}) => {
  const { t } = useTranslation();
  const { productivityReportItems } = useUserDataContext();
  const { userDetails } = useGlobalContext();

  const productivityReportMonthAggregationMap = new Map<string, ProductivityReportItem[]>();
  // For each 2-week period, we use the percentToPeers of the second week's metric as the percentile for the
  // period.
  const metricPeriodStartToPercentile = new Map<number, string>();

  // we will show either 14 weeks or 180 days. These are either 4 or 7 months
  const monthLookback = WA_WAREHOUSE_ALLOW_LIST.includes(userDetails.warehouseId) ? 7 : 4;
  let currentMonth = moment();
  for (let i = 0; i < monthLookback; i++) {
    productivityReportMonthAggregationMap.set(currentMonth.format('MMM YYYY'), []); // Add the formatted month to the array
    currentMonth = currentMonth.subtract(1, 'month'); // Move to the previous month
  }

  // TODO: Refactor this structure to accommodate the rolling 2-week period change
  if (productivityReportItems) {
    productivityReportItems.forEach((item) => {
      const month = moment(item.periodStart! * 1000).format('MMM YYYY');
      if (productivityReportMonthAggregationMap.has(month)) {
        // When handling a rolling 2-week period with Wed-Wed metrics, we must exclude any periods that have not yet
        // been released. For example, as of Tuesday, May 20, the most recent valid period is Apr 30 to May 14.
        // If a metric exists for the week of May 7 to May 14, it will be included within the April 30 to May 14
        // period, and a new period from May 7 to May 21 will not be generated.
        if (!isFourteenDaysReviewPeriod(item.periodStart!) ||
          moment.unix(item.periodStart!) < getPerformanceWeekStart().subtract(1, 'weeks')
        ) {
          productivityReportMonthAggregationMap.get(month)!.push(item);
        }
      }
      // When handling a rolling 2-week period with Wed-Wed metrics, each weekly metric must be included in
      // two rolling periods to ensure complete coverage. For example, a May 7-14 metric would be duplicated
      // and included in both Apr 30-May 14 and May 7-21 rolling periods, with the duplicate entry's period
      // start adjusted back one week.
      if (isFourteenDaysReviewPeriod(item.periodStart!) &&
        moment.unix(item.periodStart!) < getPerformanceWeekStart()
      ) {
        const newItem = { ...item };
        newItem.periodStart = item.periodStart! - (7 * 24 * 60 * 60); // Minus 1 week
        metricPeriodStartToPercentile.set(newItem.periodStart, getPercentileString(item.percentToPeers));
        const newMonth = moment(newItem.periodStart! * 1000).format('MMM YYYY');
        if (productivityReportMonthAggregationMap.has(newMonth)) {
          productivityReportMonthAggregationMap.get(newMonth)!.push(newItem);
        }
      }

      // sort the metrics of each month from newest to oldest
      productivityReportMonthAggregationMap.forEach((value, key) => {
        value.sort((a, b) => b.periodStart! - a.periodStart!);
        productivityReportMonthAggregationMap.set(key, value);
      });
    });
  }

  // Items are already sorted from newest to oldest. We will set the month to the first item
  const startingMonth = moment().format('MMM YYYY');
  const startingReportItems = productivityReportMonthAggregationMap.get(startingMonth) ?? [];
  const [selectedMonth, setSelectedMonth] = useState(startingMonth);
  const [productivityReportMonthlyItems, setProductivityReportMonthlyItems] = useState<ProductivityReportItem[]>(startingReportItems);

  const updateState = (month: string) => {
    setSelectedMonth(month);
    setProductivityReportMonthlyItems(productivityReportMonthAggregationMap.get(month)!);
  };

  const tableRows = useMemo(() => {
    if (productivityReportMonthlyItems) {
      const formattedReports = formatReportItems(productivityReportMonthlyItems);
      return formattedReports.map((report) => {
        const data = report.report;
        return (
          <TrElement>
            {report.spanningRow && (
              <TdElement rowSpan={report.rowSpan}>
                {moment(data.periodStart! * 1000).format('M/D/Y')}
              </TdElement>
            )}
            {report.spanningRow && (
              <TdElement rowSpan={report.rowSpan}>
                {moment(data.periodStart! * 1000)
                  .add(getSPPRPeriodDays(data.periodStart! * 1000), 'days')
                  .format('M/D/Y')}
              </TdElement>
            )}
            <TdElement>{data.workItem}</TdElement>
            <TdElement>{data.unitsProcessed}</TdElement>
            <TdElement>
              {data.secondsWorked ? ((data.secondsWorked || 0) / 3600).toFixed(2) : ''}
            </TdElement>
            <TdElement>{data.unitsPerHour ? data.unitsPerHour!.toFixed(2) : ''}</TdElement>
            <TdElement>{data.guardrailRate}</TdElement>
            {report.spanningRow && (
              <TdElement rowSpan={report.rowSpan}>
                {isFourteenDaysReviewPeriod(data.periodStart!) ?
                  metricPeriodStartToPercentile.get(data.periodStart!) :
                  getPercentileString(data.percentToPeers)
                }
              </TdElement>
            )}
          </TrElement>
        );
      });
    }
    return [];
  }, [productivityReportMonthlyItems, productivityReportItems]);


  if (productivityReportItems === undefined) {
    return (
      <Flex height="188px" flexDirection="row" justifyContent="center" alignItems="center">
        <Spinner showText />
      </Flex>
    );
  } else {
    return (
      <Col gridGap="S200">
        <ProductivityDisclosure/>
        <Flex flexDirection="row" justifyContent="right" alignItems="center" gridGap="S300">
          <Col alignItems="start" gridGap="S300">
            <Label htmlFor="week-selector" id="week-selector-label">
              <Text fontSize="T200" fontWeight="light" textAlign="right">
                {t('month')}
              </Text>
            </Label>
          </Col>
          <Col width={150}>
            <Select
              labelId="week-selector-label"
              id="week-selector"
              value={selectedMonth}
              onChange={(value) => updateState(value)}
              options={Array.from(productivityReportMonthAggregationMap.keys())}
            />
          </Col>
              <Col>
                <EmailReportWidget reportType="FourteenWeekProductivityReport"/>
              </Col>
        </Flex>
        {productivityReportMonthlyItems.length === 0 ?
          <Text fontWeight="light">No data available.</Text> :
          <Col>
            <TableElement>
              <TheadElement>
                <TrElement>
                  <TdElement>Period Start</TdElement>
                  <TdElement>Period End</TdElement>
                  <TdElement>Process Path</TdElement>
                  <TdElement>Units Processed</TdElement>
                  <TdElement>Hours Worked</TdElement>
                  <TdElement>
                    <Row alignItems='center'>
                      Your UPH
                      <IconWithTooltip tooltipText="Total number of units processed / Total number of hours worked" />
                    </Row>
                  </TdElement>
                  <TdElement>
                    <Row alignItems='center'>
                      75th Percentile
                      <IconWithTooltip tooltipText="The rate achieved by 75% of eligible associates who worked in that process path over the prior 5 weeks" />
                    </Row>
                  </TdElement>
                  <TdElement>
                    <Row alignItems='center'>
                      Your combined % to peers
                      <IconWithTooltip tooltipText="How your performance ranked compared to your peers across all process paths" />
                    </Row>
                  </TdElement>
                </TrElement>
              </TheadElement>
              <TbodyElement>{tableRows}</TbodyElement>
            </TableElement>
          </Col>}
      </Col>
    );
  }
};
